import type { ReactNode } from 'react';
import { useBodyClass } from '../lib/body-class';
import type { BreadcrumbItem } from './Breadcrumbs';
import PhotoHero, { type HeroAction, type HeroImage } from './ui/PhotoHero';
import TitleBand from './ui/TitleBand';

// Interior (non-home) pages remove the home-specific body class.
const REMOVE_ON_INTERIOR = ['home'];

/**
 * Interior page shell: hero + <main id="main"> + optional sidebar.
 *
 * Hero choice (BUILD-BRIEF D11):
 *  - `title` + `heroVariant="band"` (default): C3 title band with the H1.
 *  - `title` + `heroVariant="photo"` + `heroImage`: C2 photo hero.
 *  - `hero={<Custom/>}`: any node (condition pages keep their own hero).
 *  - nothing: a slim band + curve so legacy pages that still render their own
 *    <h1> in the content keep the signature edge. Pages that pass `title` must
 *    NOT render another <h1>.
 *
 *   <InteriorLayout title="About Us" lead="..." breadcrumbs={[{ name: 'About Us' }]} bodyClasses={['page-about-us']}>
 *   <InteriorLayout title="Pediatric Chiropractic" heroVariant="photo" heroImage={{ src, alt, width, height, focal }} heroPrimary={{ label: 'Book Online', to: SITE.booking.chooserPath }}>
 */
export interface InteriorLayoutProps {
  /** Extra body classes like 'page-about-us', 'page-id-350' */
  bodyClasses?: string[];
  /** Optional sidebar content (rendered inside #container alongside #content) */
  sidebar?: ReactNode;
  /** Custom hero node rendered above <main> (overrides title/heroVariant). */
  hero?: ReactNode;
  /** Page H1 rendered by the band / photo hero. */
  title?: ReactNode;
  heroVariant?: 'band' | 'photo';
  eyebrow?: ReactNode;
  lead?: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  /** Photo hero only. */
  heroImage?: HeroImage;
  heroPrimary?: HeroAction;
  heroSecondary?: HeroAction;
  heroAlign?: 'left' | 'center';
  /** Photo hero scrim override (see --wash-hero) for a bright or busy photo. */
  heroScrim?: string;
  /** Extra class on the photo hero, e.g. `ve-photo-hero--split` (photo right, navy ground left). */
  heroClassName?: string;
  /** Extra nodes inside the band/hero under the lead. */
  heroChildren?: ReactNode;
  children: ReactNode;
}

export default function InteriorLayout({
  bodyClasses = [],
  sidebar,
  hero,
  title,
  heroVariant = 'band',
  eyebrow,
  lead,
  breadcrumbs,
  heroImage,
  heroPrimary,
  heroSecondary,
  heroAlign,
  heroScrim,
  heroClassName,
  heroChildren,
  children,
}: InteriorLayoutProps) {
  const hasLayoutClass = bodyClasses.some((c) => c.startsWith('layout-'));
  useBodyClass(['not-home', 'page', 'type-page', ...(hasLayoutClass ? [] : ['layout-one-col']), ...bodyClasses], REMOVE_ON_INTERIOR);

  let heroNode: ReactNode;
  if (hero !== undefined) {
    heroNode = hero;
  } else if (title && heroVariant === 'photo' && heroImage) {
    heroNode = (
      <PhotoHero
        size="page"
        align={heroAlign}
        title={title}
        titleId="page-title"
        eyebrow={eyebrow}
        subtitle={lead}
        image={heroImage}
        breadcrumbs={breadcrumbs}
        primary={heroPrimary}
        secondary={heroSecondary}
        scrim={heroScrim}
        className={heroClassName}
      >
        {heroChildren}
      </PhotoHero>
    );
  } else if (title) {
    heroNode = (
      <TitleBand title={title} titleId="page-title" eyebrow={eyebrow} lead={lead} breadcrumbs={breadcrumbs}>
        {heroChildren}
      </TitleBand>
    );
  } else {
    heroNode = <TitleBand title="" slim />;
  }

  return (
    <>
      {heroNode}

      {/* Content area (id "ve-page" replaces the retired PP "container_wrap" hook) */}
      <div id="ve-page" className="ve-page-wrap">
        <div id="container">
          <main id="main" tabIndex={-1}>
            <div id="content">{children}</div>
          </main>
          {sidebar}
        </div>
      </div>
    </>
  );
}
